export type CompressImageOptions = {
  maxBytes?: number;
  maxDimension?: number;
  quality?: number;
  mimeType?: 'image/jpeg' | 'image/webp';
};

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob | null> =>
  new Promise((resolve) => canvas.toBlob((b) => resolve(b), type, quality));

const loadImageSource = async (
  file: File
): Promise<{ source: CanvasImageSource; width: number; height: number; cleanup?: () => void } | null> => {
  if (typeof document === 'undefined') return null;

  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(file);
    return { source: bitmap, width: bitmap.width, height: bitmap.height, cleanup: () => bitmap.close?.() };
  }

  const url = URL.createObjectURL(file);
  const img = new Image();
  img.decoding = 'async';
  img.src = url;

  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('Failed to load image'));
  });

  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  if (!width || !height) {
    URL.revokeObjectURL(url);
    return null;
  }

  return { source: img, width, height, cleanup: () => URL.revokeObjectURL(url) };
};

export async function compressImageFile(file: File, options: CompressImageOptions = {}): Promise<File> {
  const type = String(file.type || '').toLowerCase();
  if (!type.startsWith('image/')) return file;
  if (type.includes('svg')) return file;
  if (typeof document === 'undefined') return file;

  const maxBytes = options.maxBytes ?? 1_000_000;
  const maxDimension = options.maxDimension ?? 1920;
  const mimeType = options.mimeType ?? 'image/jpeg';
  const initialQuality = options.quality ?? 0.82;

  const loaded = await loadImageSource(file).catch(() => null);
  if (!loaded) return file;

  const { source, width, height, cleanup } = loaded;
  try {
    const baseScale = Math.min(1, maxDimension / Math.max(width, height));
    if (baseScale === 1 && file.size <= maxBytes) return file;

    const render = async (scale: number, quality: number): Promise<Blob | null> => {
      const targetW = Math.max(1, Math.round(width * scale));
      const targetH = Math.max(1, Math.round(height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(source, 0, 0, targetW, targetH);
      return await canvasToBlob(canvas, mimeType, quality);
    };

    let scale = baseScale;
    let quality = initialQuality;
    let blob = await render(scale, quality);
    if (!blob) return file;

    const minQuality = 0.55;
    while (blob.size > maxBytes && quality > minQuality) {
      quality = Math.max(minQuality, quality - 0.07);
      blob = await render(scale, quality);
      if (!blob) return file;
    }

    let downscaleAttempts = 0;
    while (blob.size > maxBytes && downscaleAttempts < 2) {
      downscaleAttempts += 1;
      scale = scale * 0.85;
      quality = initialQuality;
      blob = await render(scale, quality);
      if (!blob) return file;
      while (blob.size > maxBytes && quality > minQuality) {
        quality = Math.max(minQuality, quality - 0.07);
        blob = await render(scale, quality);
        if (!blob) return file;
      }
    }

    if (blob.size >= file.size) return file;

    const nextName = (() => {
      const base = file.name.replace(/\.[a-z0-9]+$/i, '');
      const ext = mimeType === 'image/webp' ? 'webp' : 'jpg';
      return `${base}.${ext}`;
    })();

    return new File([blob], nextName, { type: mimeType, lastModified: file.lastModified });
  } finally {
    cleanup?.();
  }
}
